<template>
    <table class="w-full text-sm border border-gray-300 table-fixed dark:border-zinc-900">
        <thead class="bg-slate-50 dark:bg-slate-700">
            <tr>
                <th
                    v-for="dimension in dimensions"
                    :key="dimension.column"
                    class="px-4 py-2 text-sm text-left border-b cursor-pointer select-none bg-gray-50 hover:bg-white dark:bg-neutral-900 dark:hover:bg-black dark:border-neutral-800 border-neutral-200"
                    :class="{
                        'bg-blue-50': orderBy === dimension.column,
                    }"
                    scope="col"
                    @click="order(dimension)"
                >
                    <div class="flex items-center justify-between">
                        <span class="flex-auto">
                            {{ dimension.column }}
                        </span>
                        <span v-if="orderBy === dimension.column && orderWay === Way.DESC" class="grow-0">
                            <icon icon="hero/outline/chevron-down" class="w-3 h-3"></icon>
                        </span>
                        <span v-else-if="orderBy === dimension.column && orderWay === Way.ASC" class="grow-0">
                            <icon icon="hero/outline/chevron-up" class="w-3 h-3"></icon>
                        </span>
                        <span v-else class="grow-0 opacity-30">
                            <icon icon="hero/outline/selector" class="w-3 h-3"></icon>
                        </span>
                    </div>
                </th>
            </tr>
        </thead>
        <tbody>
            <template v-for="(group, groupIndex) in groups" :key="groupIndex">
                <tr
                    v-for="(row, rowIndex) in group.rows"
                    :key="`${groupIndex}-${rowIndex}`"
                    class="group"
                    :class="{ 'border-t-2 border-neutral-300 dark:border-neutral-600': rowIndex === 0 && groupIndex > 0 }"
                >
                    <template v-for="dimension in dimensions" :key="dimension.column">
                        <td
                            v-if="dimension.column === groupBy && rowIndex === 0"
                            :rowspan="group.rows.length"
                            class="px-4 py-2 text-sm font-semibold align-top border-b bg-neutral-50 dark:bg-neutral-800/50 dark:border-neutral-900 border-neutral-200"
                        >
                            {{ row[dimension.column] }}
                        </td>
                        <td
                            v-else-if="dimension.column !== groupBy"
                            class="px-4 py-2 text-sm border-b dark:border-neutral-900 border-neutral-200"
                        >
                            {{ row[dimension.column] }}
                        </td>
                    </template>
                </tr>
            </template>
        </tbody>
    </table>
</template>

<script lang="ts" setup>
import { toRef, Ref, computed } from 'vue';
import { Way } from '@src/Reports/Pages/enums';

const props = defineProps<{
    dimensions: any[];
    rows: any[];
    groupBy?: string;
    orderBy?: string;
    orderWay?: Way;
}>();

const dimensions = toRef(props, 'dimensions');
const rows = toRef(props, 'rows');
const orderBy: Ref<string> = toRef(props, 'orderBy');
const orderWay: Ref<Way> = toRef(props, 'orderWay');

const groups = computed(() => {
    if (!props.groupBy || !rows.value) {
        return [{ key: null, rows: rows.value }];
    }

    const groupMap = new Map<string, any[]>();

    for (const row of rows.value) {
        const key = String(row[props.groupBy] ?? '');
        if (!groupMap.has(key)) {
            groupMap.set(key, []);
        }
        groupMap.get(key).push(row);
    }

    return Array.from(groupMap.entries()).map(([key, rows]) => ({ key, rows }));
});

const emit = defineEmits<{
    (event: 'order', payload: { column: string; way: Way }): void;
}>();

const order = (dimension: any) => {
    if (props.orderBy === dimension.column) {
        emit('order', {
            column: dimension.column,
            way: props.orderWay === Way.ASC ? Way.DESC : Way.ASC,
        });
    } else {
        emit('order', {
            column: dimension.column,
            way: Way.ASC,
        });
    }
};
</script>
